import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const isAdd = event.action === 'add' && !(/** @type {any} */ (event).checkoutRedirect);

    // Desktop (≥750px): the cart is a standalone page, not a drawer. The product
    // form navigates to /cart off the add response (see product-form.js
    // #leavesForCartPage) — so here we just suppress the drawer so it never
    // flashes open in the beat before that navigation. Gated on `auto-open` so
    // it never fires on the /cart page itself (which drops the attribute).
    if (isAdd && this.hasAttribute('auto-open') && window.matchMedia('(min-width: 750px)').matches) {
      return;
    }

    // Buy now adds (checkoutRedirect) navigate to checkout immediately —
    // opening here would just flash the drawer for a beat before the redirect.
    const shouldAutoOpen =
      this.hasAttribute('auto-open') &&
      event.action === 'add' &&
      !(/** @type {any} */ (event).checkoutRedirect) &&
      !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    // Open immediately at dispatch — even from an empty cart, where the real
    // line items only exist after the server responds. The [adding] attribute
    // swaps the "Your cart is empty" state for skeleton rows for that beat;
    // its value is the incoming line count (one row per distinct variant,
    // matching what the cart will render — same-variant units merge into one
    // line). Markup ships 3 rows, so cap there.
    const emptyBeforeAdd = this.#isCartEmpty();
    if (shouldAutoOpen && !sourceModal) {
      if (emptyBeforeAdd) {
        const lineCount = Math.min(Math.max(event.lines?.length ?? 1, 1), 3);
        this.setAttribute('adding', String(lineCount));
      }
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ cart, detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (detail?.didError) {
          this.#settleFailedAdd(emptyBeforeAdd);
        } else {
          // Success: the morph replaces the empty-state DOM (skeleton included),
          // so drop [adding] a couple frames later to avoid re-flashing the
          // empty message before the new content lands.
          requestAnimationFrame(() => requestAnimationFrame(() => this.removeAttribute('adding')));
        }

        // Emptying the cart closes the drawer instead of leaving the empty
        // state on screen — the page behind it is the natural "continue
        // shopping". Any mutation may land at zero (remove, update, clear),
        // so gate on the confirmed count, not the action. Only on
        // server-confirmed responses, so a failed request never closes over
        // a still-filled cart.
        const itemCount = cart?.totalQuantity ?? detail?.itemCount;
        if (!detail?.didError && itemCount === 0 && this.#themeDrawer?.isOpen) {
          this.#themeDrawer.close();
          settle();
          return;
        }

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        this.#settleFailedAdd(emptyBeforeAdd);
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  /**
   * A failed add leaves nothing to show — drop the skeleton, and if the drawer
   * was opened over an empty cart, close it again so the form's error message
   * behind it is visible.
   *
   * @param {boolean} emptyBeforeAdd - Whether the cart was empty at dispatch.
   */
  #settleFailedAdd(emptyBeforeAdd) {
    this.removeAttribute('adding');
    if (emptyBeforeAdd && this.#isCartEmpty() && this.#themeDrawer?.isOpen) {
      this.#themeDrawer.close();
    }
  }

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
